import React, {
    useEffect, useState, useCallback, Fragment,
} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useParams, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import format from 'date-fns/format';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'lodash';
import CustomTable from '../../components/Table/Table';
import Button from '../../components/Button/CustomButton';
import GridItem from '../../components/Grid/GridItem';
import styles from '../../assets/jss/pages/productManagementPageStyle';
import CustomTypography from '../../components/Typography/CustomTypography';
import SingleDatePickerSearch from '../../components/DatePicker/SingleDatePickerSearch';
import GridContainer from '../../components/Grid/GridContainer';
import CleanableInput from '../../components/Input/CleanableInput';
import Loader from '../../components/Loader/Loader';
import RegisterLinks from '../../components/RegisterLinks/RegisterLinks';
import dateUtils from '../../utils/dateUtils';
import { navyColor } from '../../assets/jss/global';
import ViewHeader from '../../components/Header/ViewHeader';
import {
    LoadMoreIcon,
    RadioButtonIcon,
} from '../../components/Icons/Icon';
import FreeLogin from '../../components/FreeEntrance/FreeLogin';
import ListSeminarViewerNonAuthCustomLayout from './ListSeminarViewerNonAuthCustomLayout';
import { platform, PLATFORM } from '../../utils/getPlatform';

const useStyles = makeStyles(styles);

export const Header = (props) => {
    const { eventInfo } = props;
    const intl = useIntl();
    return (
        <>
            {!_.isEmpty(eventInfo) && (
                <ViewHeader
                    eventName={eventInfo.name}
                    title={eventInfo?.settings_texts_sidebar_viewer?.field_name?.seminar_list || intl.formatMessage({ id: 'userRoutes.seminarList' })}
                    eventInfo={eventInfo}
                />
            )}
        </>
    );
};

Header.defaultProps = {
    eventInfo: {},
};

Header.propTypes = {
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
};

const ListSeminarViewerNonAuth = (props) => {
    const {
        listPagination,
        fetchListSeminarViewer,
        listSeminarViewer,
        clearListSeminarViewer,
        isLoading,
        eventInfo,
        isAuthorized,
        isWeakAuth,
        isCheckEvent,
        customText,
        customColor,
        locale,
        language,
        langOther,
    } = props;
    const classes = useStyles(props);
    const routeParams = useParams();
    const { shortName } = routeParams;
    const intl = useIntl();
    const history = useHistory();
    const [listSeminarViewerResult, setlistSeminarViewerResult] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);
    const [showSearch, setShowSearch] = useState(false);
    const [params, setParams] = useState({ startDate: '', endDate: '', seminarTitle: '' });
    useEffect(() => {
        if (isAuthorized && (!_.isEmpty(eventInfo) && eventInfo.free_seminar)) {
            history.push(`/${shortName}/seminars`);
        }
    }, [isAuthorized, history, routeParams, eventInfo]);

    useEffect(() => {
        if (langOther !== 'English' && langOther !== '日本語' && langOther !== '') {
            setShowSearch(true);
        } else setShowSearch(false);
    }, [langOther]);

    useEffect(() => {
        if (shortName) {
            if (params.endDate || params.startDate) {
                fetchListSeminarViewer({
                    event_name: shortName,
                    sdate: params.startDate ? format(new Date(params.startDate), 'yyyy-MM-dd') : '',
                    edate: params.endDate ? format(new Date(params.endDate), 'yyyy-MM-dd') : '',
                    language: locale,
                });
            } else {
                fetchListSeminarViewer({
                    event_name: shortName,
                    language: locale,
                });
            }
        }
        return () => clearListSeminarViewer();
    }, [shortName, locale]);

    useEffect(() => {
        setParams({ ...params, seminarTitle: '' });
    }, [locale]);

    useEffect(() => {
        if (listSeminarViewer) {
            setlistSeminarViewerResult(listSeminarViewer);
        }
    }, [listSeminarViewer]);

    useEffect(() => {
        if (listPagination) {
            setCurrentPage(listPagination.page);
            setTotalPage(listPagination.total_page);
        }
    }, [listPagination]);

    const handleSearch = () => {
        clearListSeminarViewer();
        fetchListSeminarViewer({
            event_name: shortName,
            sdate: params.startDate ? format(new Date(params.startDate), 'yyyy-MM-dd') : '',
            edate: params.endDate ? format(new Date(params.endDate), 'yyyy-MM-dd') : '',
            title: params.seminarTitle ? params.seminarTitle.trim() : '',
            language: locale,
        });
    };

    const handleLoadMore = () => {
        fetchListSeminarViewer({ event_name: shortName, page: currentPage + 1, language: locale });
    };

    const handleSetParams = useCallback((field, value) => {
        setParams((prevParams) => ({ ...prevParams, [field]: value }));
    }, []);

    const handleRedirectRegisterSeminar = () => {
        history.push(`/${shortName}/register-visitor`);
    };

    const createDataView = () => {
        const viewTotal = [];
        (Object.keys(listSeminarViewerResult).forEach((key) => {
            createDateTitle(key);
            const viewTable = [];
            Object.keys(listSeminarViewerResult[key]).forEach((roomData) => {
                const tableData = Object.keys(listSeminarViewerResult[key][roomData]).map((index) => {
                    const seminar = listSeminarViewerResult[key][roomData][index];
                    return [
                        `${seminar.stime}~${seminar.etime}`,
                        seminar.title,
                        seminar.content,
                        seminar.speaker,
                        seminar.cost === 0
                            ? intl.formatMessage({ id: 'common.free' })
                            : `${intl.formatNumber(seminar.cost)}${intl.formatMessage({ id: 'common.yen' })}`,
                    ];
                });
                viewTable.push(createTable(roomData, tableData));
            });
            const viewDate = (
                <React.Fragment key={key}>
                    {createDateTitle(key)}
                    {viewTable}
                </React.Fragment>
            );
            viewTotal.push(viewDate);
        }));
        return viewTotal;
    };

    const createDateTitle = (dateTitle) => (
        <GridItem xs={12} style={{ margin: '20px 0 20px 0' }}>
            <CustomTypography bold variant="h5" customColor={customColor?.contents_text_color}>
                {intl.formatMessage({ id: 'common.date' })}
                {'  :  '}
                {dateUtils.formatJa(new Date(dateTitle), locale)}
            </CustomTypography>
        </GridItem>

    );

    const createTable = (title, viewlist) => (
        <GridItem xs={12} key={title}>
            <CustomTypography
                variant="subtitle1"
                bold
                customColor={customColor?.contents_text_color}
            >
                <li>
                    {intl.formatMessage({ id: 'common.conferenceRoom' })}
                    {' '}
                    {title}
                </li>
            </CustomTypography>
            <CustomTable
                cellTextColorHear={customColor?.list_table?.title_text_color}
                cellTextColorBody={customColor?.contents_text_color}
                cellBackgroundColor={customColor?.list_table}
                tableHead={[
                    customText?.field_name?.time || intl.formatMessage({ id: 'common.time1' }),
                    customText?.field_name?.lecture_name || intl.formatMessage({ id: 'seminar.lectureName' }),
                    customText?.field_name?.lecture_content || intl.formatMessage({ id: 'seminar.lectureContent' }),
                    customText?.field_name?.speaker || intl.formatMessage({ id: 'seminar.speaker' }),
                    customText?.field_name?.price || intl.formatMessage({ id: 'price' }),
                ]}
                tableHeadWidth={['15%', '20%', '20%', '25%', '10%']}
                tableData={viewlist}
                cellColor={customColor?.list_table?.title_background_color || navyColor}
                hover={false}
            />
        </GridItem>
    );

    const createCustomView = () => (
        <ListSeminarViewerNonAuthCustomLayout
            listData={listSeminarViewerResult}
            customColor={customColor}
            locale={locale}
        />
    );

    const renderSearchContent = (text, content) => (
        <GridContainer style={{ marginTop: '20px' }}>
            <GridItem xs={12} sm={6} md={7} className={classes.inputTitle}>
                <CustomTypography bold style={{ marginTop: '10px' }} customColor={customColor?.contents_text_color}>
                    {text}
                </CustomTypography>
            </GridItem>
            <GridItem xs={12} sm={6} md={5} style={{ paddingLeft: '8px' }}>
                {content}
            </GridItem>
        </GridContainer>
    );

    return (
        <>
            <Loader isLoading={isLoading} />
            {!_.isEmpty(eventInfo) && (
                <div className={classes.wrapper}>
                    <GridItem xs={12} className={classNames({ [classes.noPadding]: platform !== PLATFORM.OTHER && eventInfo.layout_seminar })}>
                        {!showSearch && (
                            <GridContainer style={{ marginTop: '20px' }}>
                                <GridItem xs={12} sm={6} md={7} className={classes.inputTitle}>
                                    <CustomTypography bold customColor={customColor?.contents_text_color}>
                                        {intl.formatMessage({ id: 'common.date' })}
                                    </CustomTypography>
                                </GridItem>
                                <GridItem xs={12} sm={6} md={5} className={classes.dateRangeSearch}>
                                    <SingleDatePickerSearch
                                        onChange={(startDate) => handleSetParams('startDate', startDate)}
                                        defaultValue={null}
                                    />
                                    <span className={classes.dateRangeArea}>〜</span>
                                    <SingleDatePickerSearch
                                        onChange={(endDate) => handleSetParams('endDate', endDate)}
                                        defaultValue={null}
                                    />
                                </GridItem>
                            </GridContainer>
                        )}
                        {!showSearch && (
                            renderSearchContent(
                                customText?.field_name?.lecture_name || intl.formatMessage({ id: 'seminar.lectureName' }),
                                <CleanableInput
                                    classes={{ root: classes.colorTextInput }}
                                    className={classes.input}
                                    size="small"
                                    defaultValue={params.seminarTitle}
                                    placeholder={
                                        intl.formatMessage({ id: 'seminar.placeholderSearch' }, {
                                            courseContent: customText?.field_name?.lecture_name || intl.formatMessage({ id: 'seminar.lectureName' }).toLocaleLowerCase(),
                                        })
                                    }
                                    onChange={(value) => handleSetParams('seminarTitle', value)}
                                />,
                            )
                        )}
                        {!showSearch && (
                            <GridContainer>
                                <GridItem xs={12} md={12} className={classes.buttonSearch}>
                                    <Button
                                        color="navy"
                                        round
                                        gradient
                                        size="md"
                                        onClick={() => handleSearch()}
                                        buttonTextColor={customColor?.button_text_color}
                                        buttonBackgroundColor={customColor?.button_background_color?.common_button}
                                    >
                                        <b>{customText?.button_name?.search_seminar || intl.formatMessage({ id: 'search' })}</b>
                                    </Button>
                                </GridItem>
                            </GridContainer>
                        )}
                        {!eventInfo.layout_seminar ? createDataView() : createCustomView()}
                        {currentPage < totalPage && (
                            <GridItem align={!eventInfo.layout_seminar ? 'right' : 'center'} className={eventInfo.layout_seminar && classes.loadMore}>
                                <Button
                                    onClick={handleLoadMore}
                                    color="navy"
                                    gradient
                                    size="md"
                                    round
                                    buttonTextColor={customColor?.button_text_color}
                                    buttonBackgroundColor={customColor?.button_background_color?.common_button}
                                >
                                    <LoadMoreIcon />
                                    {intl.formatMessage({ id: 'common.seeMore' })}
                                </Button>
                            </GridItem>
                        )}
                        {
                            !listPagination.total_length && (
                                <GridItem
                                    xs={12}
                                    style={{
                                        textAlign: 'center',
                                        fontWeight: 'bold',
                                        fontSize: '20px',
                                        zIndex: 1,
                                        color: customColor?.contents_text_color,
                                    }}
                                >
                                    <FormattedMessage id="noData" />
                                </GridItem>
                            )
                        }
                        {
                            isCheckEvent && !!(!eventInfo.free_seminar && !eventInfo.free_exhibition && !eventInfo.free_live_stream && !eventInfo.outer_server_activation) && (
                                <RegisterLinks />
                            )
                        }
                        <GridContainer style={{ marginTop: '20px' }}>
                            {
                                !isAuthorized && (!isWeakAuth || isWeakAuth === 'false') && eventInfo.free_seminar && (
                                    <FreeLogin
                                        customColor={customColor}
                                    />
                                )
                            }
                            {
                                (!eventInfo.free_seminar
                                    && !(!eventInfo.free_seminar && !eventInfo.free_exhibition && !eventInfo.free_live_stream)
                                    && !eventInfo.outer_server_activation) && (
                                    <GridItem container item className={classes.sectionWrapper}>
                                        <GridItem xs={12} lg={6}>
                                            <div className={classes.centerToRight}>
                                                <Button
                                                    color="info"
                                                    gradient
                                                    className={classes.buttonBig2}
                                                    round
                                                    onClick={handleRedirectRegisterSeminar}
                                                    buttonTextColor={eventInfo?.settings_colors_viewer?.button_text_color}
                                                    buttonBackgroundColor={eventInfo?.settings_colors_viewer?.button_background_color?.viewer}
                                                >
                                                    {intl.formatMessage({ id: 'exhibition.apply' })}
                                                </Button>
                                            </div>
                                        </GridItem>
                                        <GridItem xs={12} lg={6}>
                                            <Box display="flex" className={classes.helperText}>
                                                <RadioButtonIcon color={customColor?.button_background_color?.viewer || '#4facfe'} />
                                                <CustomTypography variant="subtitle1" customColor={customColor?.contents_text_color}>
                                                    {intl.formatMessage({ id: 'seminar.noteRegister' }, {
                                                        seminarList: eventInfo.settings_texts_sidebar_viewer?.field_name?.seminar_list
                                                        || intl.formatMessage({ id: 'home.seminar' }).toLocaleLowerCase(),
                                                    })}
                                                </CustomTypography>
                                            </Box>
                                        </GridItem>
                                    </GridItem>
                                )
                            }
                        </GridContainer>
                    </GridItem>
                </div>
            )}
        </>
    );
};

ListSeminarViewerNonAuth.defaultProps = {
    listSeminarViewer: [],
    listPagination: {},
    eventInfo: {},
    isAuthorized: false,
    isWeakAuth: false || '',
    isCheckEvent: false,
    customText: {},
    customColor: {},
    locale: 'ja',
    language: 'ja',
    fetchListSeminarViewer: () => {},
    langOther: '',
};

ListSeminarViewerNonAuth.propTypes = {
    fetchListSeminarViewer: PropTypes.func,
    clearListSeminarViewer: PropTypes.func.isRequired,
    listSeminarViewer: PropTypes.oneOfType([() => null, PropTypes.array]),
    isLoading: PropTypes.bool.isRequired,
    listPagination: PropTypes.oneOfType([() => null, PropTypes.object]),
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    isAuthorized: PropTypes.bool,
    isWeakAuth: PropTypes.oneOfType([() => PropTypes.string, PropTypes.bool]),
    isCheckEvent: PropTypes.bool,
    customText: PropTypes.oneOfType([() => null, PropTypes.object]),
    customColor: PropTypes.oneOfType([() => null, PropTypes.object]),
    locale: PropTypes.string,
    language: PropTypes.string,
    langOther: PropTypes.string,
};

export default ListSeminarViewerNonAuth;
